import { useState, useEffect, useContext, useId } from "react";
import { leadSourceList } from '../../../shared/grailConstants';
import { Controller, UseFormReturn } from "react-hook-form";
import { Trash2Icon } from "lucide-react";

import CampaignSelect from '../../../components/CampaignSelect';
import { asIdList } from '../../../shared/asIdList';
import { Campaign, CampaignType, Platform, User } from '../../../shared/types';
import { UserContext } from "../../../services/firebase";
import MultiSelect from '../../../components/MultiSelect';
import ManagerSelect from '../../../components/ManagerSelect';
import { FormCheckbox, FormCheckboxGroup } from "@/components/forms/FormCheckbox";
import { FormFieldShell } from "@/components/forms/FormField";
import { FormInput } from "@/components/forms/FormInput";
import { FormRadio, FormRadioGroup } from "@/components/forms/FormRadio";
import { FormTextarea } from "@/components/forms/FormTextarea";
import { LogoIcon } from "../../../components/LogoIcon";
import { TextLink } from "@/components/typography";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";

interface BookingFormProps {
    booker?: User,
    filter: { campaignType: CampaignType },
    campaigns: Campaign[],
    platforms: Platform[],
    reactHookForm: UseFormReturn,
    salesTeam?: User[],
}

function BookingForm(props: BookingFormProps) {
    const { reactHookForm, booker, filter, campaigns, platforms } = props;
    const authenticatedUser = useContext(UserContext);
    const isManager = authenticatedUser?.roles?.manager;
    const isBrandTeam = authenticatedUser?.roles?.brandTeam;
    const isSalesTeam = authenticatedUser?.roles?.salesTeam;
    const isAdmin = authenticatedUser?.roles?.admin;

    // 
    const { control, register, setValue, clearErrors, formState: { errors }, watch } = reactHookForm;
    const [fileUrl, setFileUrl] = useState<string>();
    const briefFileId = useId();

    const [selectedCampaignId, setSelectedCampaignId] = useState<string>();
    const [selectedCampaign, setSelectedCampaign] = useState<Campaign>();

    const leadSourceIdRequired = Boolean(watch('leadSource')?.includes('CRM Lead'));
    const salesTeamRequired = Boolean(watch('leadSource')?.includes('Sales Team'));

    // When campaign changes, fully replace campaign-derived form values (not only on first select).
    // Creatable "new" campaigns are not in `campaigns`, so they must clear prior values explicitly.
    // Do not shouldValidate here — that shows errors before submit and can leave them stuck
    // under mode: 'onSubmit'. clearErrors so a prior failed submit doesn't leave stale messages
    // on the replaced values; handleSubmit validates on the next submit as usual.
    useEffect(() => {
        if (!selectedCampaignId) {
            return;
        }

        const campaign = campaigns.find((c: Campaign) => c.id === selectedCampaignId);
        const derivedFields = ['soundUrl', 'details', 'crmLeadId', 'leadSource', 'salesTeamId'] as const;

        if (campaign?.id) {
            setValue('soundUrl', campaign.soundUrl || "");
            setValue('details', campaign.details || "");
            setValue('crmLeadId', campaign.crmLeadId || "");
            setValue('leadSource', campaign.leadSource || []);
            setValue('salesTeamId', asIdList(campaign.salesTeamId));
            clearErrors([...derivedFields]);
            setFileUrl(campaign.fileUrl || undefined);
            setSelectedCampaign(campaign);
            return;
        }

        // New / unknown campaign id — drop state that belonged to the previous campaign.
        setValue('soundUrl', "");
        setValue('details', "");
        setValue('crmLeadId', "");
        setValue('leadSource', []);
        setValue('salesTeamId', []);
        clearErrors([...derivedFields]);
        setFileUrl(undefined);
        setSelectedCampaign(undefined);
    }, [selectedCampaignId, campaigns, setValue, clearErrors])

    return (
        <section className="mx-auto max-w-175 space-y-4 p-4">
            <Card size="sm">
                <CardHeader>
                    <CardTitle>Your Details</CardTitle>
                </CardHeader>
                <CardContent>
                    <FieldGroup className="gap-4">
                {booker?.firstName && booker?.lastName && booker?.company && booker?.email ?
                    <>
                        <div className="divide-y divide-border">
                            <div className="flex justify-between py-2">
                                <strong>Name</strong>
                                <div>{booker?.firstName} {booker?.lastName}</div>
                            </div>
                            <div className="flex justify-between py-2">
                                <strong>Email</strong>
                                <div>{booker?.email}</div>
                            </div>
                            <div className="flex justify-between py-2">
                                <strong>Company</strong>
                                <div>{booker?.company}</div>
                            </div>
                            <div className="flex justify-between py-2">
                                <strong>Phone</strong>
                                <div>{booker?.phone || '–'}</div>
                            </div>
                        </div>
                        <input type="hidden" value={booker?.firstName} {...register("firstName", { required: true })} />
                        <input type="hidden" value={booker?.lastName} {...register("lastName", { required: true })} />
                        <input type="hidden" value={booker?.email} {...register("email", { required: true })} />
                        <input type="hidden" value={booker?.company} {...register("company", { required: true })} />
                        <input type="hidden" value={booker?.phone || ""} {...register("phone", { required: false })} />
                    </>
                    :
                    <>
                        {/* Side-by-side name fields: equal-width row on wider viewports */}
                        <div className="flex flex-wrap gap-4">
                            <FormInput
                                label="First Name"
                                required
                                fieldClassName="min-w-0 grow basis-auto"
                                placeholder="First Name"
                                defaultValue={booker?.firstName}
                                error={errors.firstName ? "First name is required." : undefined}
                                {...register("firstName", { required: true })}
                            />

                            <FormInput
                                label="Last Name"
                                required
                                fieldClassName="min-w-0 grow basis-auto"
                                placeholder="Last Name"
                                defaultValue={booker?.lastName}
                                error={errors.lastName ? "Last name is required." : undefined}
                                {...register("lastName", { required: true })}
                            />
                        </div>

                        <FormInput
                            label="Email"
                            required
                            type="email"
                            placeholder="Email"
                            defaultValue={booker?.email}
                            error={errors.email ? "Valid Email is required." : undefined}
                            {...register("email", { required: true, pattern: { value: /^[a-zA-Z0-9.!#$%&'*+=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/, message: "Enter a valid email." } })}
                        />

                        <FormInput
                            label="Company"
                            required
                            placeholder="Company"
                            defaultValue={booker?.company}
                            error={errors.company ? "Company is required." : undefined}
                            {...register("company", { required: true })}
                        />

                        <FormInput
                            label="Phone Number"
                            placeholder="Phone"
                            defaultValue={booker?.phone}
                            error={errors.phone ? "Phone Number is required." : undefined}
                            {...register("phone", { required: false })}
                        />
                    </>
                }
                    </FieldGroup>
                </CardContent>
            </Card>

            <Card size="sm">
                <CardHeader>
                    <CardTitle>Booking Details</CardTitle>
                </CardHeader>
                <CardContent>
                    <FieldGroup className="gap-4">
                <Controller
                    name="campaign"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { name, onBlur, onChange, ref, value } }) => (
                        <FormFieldShell
                            label="Campaign Name"
                            required
                            htmlFor="campaign"
                            invalid={Boolean(errors.campaign)}
                            error={errors.campaign ? "Campaign Name is required." : undefined}
                        >
                            {(controlProps) => (
                                <CampaignSelect
                                    id={controlProps.id}
                                    name={name}
                                    inputRef={ref}
                                    onBlur={onBlur}
                                    aria-describedby={controlProps["aria-describedby"]}
                                    isErrored={Boolean(errors.campaign)}
                                    value={value || null}
                                    onChange={(newVal) => { onChange(newVal); setSelectedCampaignId(newVal?.id || "new") }}
                                    placeholder="Old Town Road - Lil Nas X + Billy Ray Cyrus"
                                    options={campaigns?.map((campaign: Campaign) => { return { id: campaign.id, label: campaign.campaignName || "Unnamed Campaign" } })} />
                            )}
                        </FormFieldShell>
                    )}
                />
                <Field>
                    <FieldLabel>Platform</FieldLabel>
                    {/* Side-by-side icon + label */}
                    <div className="flex flex-wrap items-center gap-3">
                        {platforms.map((platform) => (
                            <span key={platform} className="flex items-center gap-1">
                                <LogoIcon brand={platform} /> {platform}
                            </span>
                        ))}
                    </div>
                </Field>
                {filter.campaignType === 'Song' ?
                    <FormInput
                        label="Primary Sound URL"
                        required
                        type="text"
                        readOnly={Boolean(selectedCampaignId && selectedCampaign?.soundUrl)}
                        placeholder="https://www.tiktok.com/music/Old-Town-Road-6683330941219244813"
                        error={
                            errors.soundUrl
                                ? errors.soundUrl.message
                                    ? (
                                        <span>
                                            Use: &nbsp;&nbsp;<strong>https://www.tiktok.com/music/...</strong>&nbsp;&nbsp;or&nbsp;&nbsp; <strong>https://vm.tiktok.com/...</strong>&nbsp;&nbsp;or&nbsp;&nbsp;<strong>https://www.instagram.com/reels/audio/...</strong>&nbsp;&nbsp;or&nbsp;&nbsp;<strong>https://www.youtube.com/source/...</strong>
                                        </span>
                                    )
                                    : "Primary Sound URL is required."
                                : undefined
                        }
                        {...register("soundUrl", {
                            required: true,
                            setValueAs: (val) => {
                                try {
                                    const url = decodeURI(val);
                                    return url;
                                } catch (e) {
                                    console.log(`error trying decodeURI`, val, e);
                                    return val;
                                }
                            },
                            pattern: { value: /^(https:\/\/www\.tiktok\.com\/music\/[A-Za-zÀ-ÖØ-öø-ÿ0-9-']*[0-9]|https:\/\/vm\.tiktok\.com\/[A-Za-zÀ-ÖØ-öø-ÿ0-9-]*|https:\/\/www\.instagram\.com\/reels\/audio\/[A-Za-zÀ-ÖØ-öø-ÿ0-9-]*|https:\/\/www\.youtube\.com\/source\/*|https:\/\/youtube\.com\/source\/*)/, message: "Valid URLs start with https://www.tiktok.com/music/ OR https://vm.tiktok.com/ OR https://www.youtube.com/source/" },
                            // pattern: selectedCampaignId ? { value: /./, message: 'Oops' } : { value: /^(https:\/\/www\.tiktok\.com\/music\/[A-Za-z0-9-']*[0-9]|https:\/\/vm\.tiktok\.com\/[A-Za-z0-9-]*)/, message: "Valid URLs start with https://www.tiktok.com/music/ OR https://vm.tiktok.com/" }
                        })}
                    />
                    : null}
                {/* {isManager && !isBrandTeam ? null : ( */}
                <>
                    <FormTextarea
                        label="Campaign Details, Deliverables, & Timeline"
                        required={!isManager}
                        description={
                            !isExactlyTikTok(platforms) && filter.campaignType !== "Song"
                                ? (
                                    <span className="text-destructive italic">
                                        <strong className="uppercase">Important:</strong> Be sure to specify post types!
                                    </span>
                                )
                                : undefined
                        }
                        placeholder="What type of content are you looking for?"
                        error={errors.details ? "Campaign Details are required." : undefined}
                        {...register("details", {
                            required: isManager ? false : true,
                        })}
                    />

                    {fileUrl ? (
                        <FormFieldShell label="Brief" htmlFor={briefFileId}>
                            {() => (
                                // Side-by-side link + action button
                                <div className="flex items-center gap-4">
                                    <TextLink
                                        id={briefFileId}
                                        href={fileUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Existing File
                                    </TextLink>
                                    <Button
                                        type="button"
                                        variant="destructive"
                                        onClick={() => setFileUrl(undefined)}
                                    >
                                        <Trash2Icon />
                                        Replace File
                                    </Button>
                                </div>
                            )}
                        </FormFieldShell>
                    ) : (
                        <FormInput
                            label="Brief"
                            type="file"
                            accept="image/*,.pdf,.doc,.docx"
                            {...register("file", { required: false })}
                        />
                    )}
                </>
                {/* )} */}
                {isBrandTeam ? (
                    <>
                        <Controller
                            name="sendCampaignReceipt"
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                                <FormRadioGroup
                                    label={`Email Receipt to booker (${booker?.email})?`}
                                    required
                                    value={field.value ?? null}
                                    onChange={field.onChange}
                                    invalid={Boolean(errors.sendCampaignReceipt)}
                                    error={errors.sendCampaignReceipt ? "Choose Yes or No." : undefined}
                                >
                                    <FormRadio value="true" label="Yes" />
                                    <FormRadio value="false" label="No" />
                                </FormRadioGroup>
                            )}
                        />

                        <Controller
                            name="sendBookingOpp"
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                                <FormRadioGroup
                                    label="Email Opportunity to Managers?"
                                    required
                                    value={field.value ?? null}
                                    onChange={field.onChange}
                                    invalid={Boolean(errors.sendBookingOpp)}
                                    error={errors.sendBookingOpp ? "Choose Yes or No." : undefined}
                                >
                                    <FormRadio value="true" label="Yes" />
                                    <FormRadio value="false" label="No" />
                                </FormRadioGroup>
                            )}
                        />
                    </>
                ) : null}
                {(isSalesTeam || isAdmin) ? <>
                    <Field className="gap-1">
                        <FieldLabel>Lead Source</FieldLabel>
                        {selectedCampaignId ?
                            <Controller
                                name="leadSource"
                                control={control}
                                defaultValue={selectedCampaign?.leadSource || []}
                                render={({ field: { onChange, value } }) => (
                                    <MultiSelect
                                        options={leadSourceList}
                                        onChange={onChange}
                                        value={value || []}
                                        isErrored={Boolean(errors.leadSource)}
                                    />
                                )}
                            /> :
                            <div>Select a campaign to set the lead source.</div>
                        }
                    </Field>
                    {leadSourceIdRequired && <>
                        <FormInput
                            label="Twenty CRM Lead ID"
                            required
                            type="text"
                            error={errors.crmLeadId ? "Lead Source ID is required when lead sources include 'CRM Lead'." : undefined}
                            {...register("crmLeadId", { required: true })}
                        />
                    </>}
                    {salesTeamRequired && <>
                        <Controller
                            name="salesTeamId"
                            control={control}
                            defaultValue={[]}
                            rules={{ validate: (value) => asIdList(value).length > 0 }}
                            render={({ field: { onChange, value } }) => (
                                <FormFieldShell
                                    label="Sales Team Member"
                                    required
                                    htmlFor="salesTeamId"
                                    invalid={Boolean(errors.salesTeamId)}
                                    error={errors.salesTeamId ? "Sales Team Member is required." : undefined}
                                >
                                    {() => (
                                        <ManagerSelect
                                            managers={props.salesTeam || []}
                                            value={asIdList(value)}
                                            onChange={(_event: unknown, next: string[]) => { onChange(next); }}
                                        />
                                    )}
                                </FormFieldShell>
                            )}
                        />
                    </>}
                </> : null}
                <>
                    <Controller
                        name="boostInterest"
                        control={control}
                        render={({ field }) => (
                            <FormCheckboxGroup label="Are you interested in Boosting posts for this campaign?">
                                <FormCheckbox
                                    label="Yes, please reach out to me about Boosting"
                                    checked={Boolean(field.value)}
                                    onChange={field.onChange}
                                    ref={field.ref}
                                    onBlur={field.onBlur}
                                />
                            </FormCheckboxGroup>
                        )}
                    />
                </>
                {!isManager && !booker?.clientWideSettings?.paymentTerms?.termsAccepted ? <>
                    <Controller
                        name="paymentTerms.termsAccepted"
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                            <FormCheckboxGroup
                                label={
                                    <>
                                        Do you agree to the&nbsp;
                                        <TextLink href="https://grail-talent.com/payment-terms" target="_blank" rel="noreferrer">
                                            Grail Talent Payment terms
                                        </TextLink>
                                        ?
                                    </>
                                }
                                required
                            >
                                <FormCheckbox
                                    label="Yes, I agree"
                                    required
                                    checked={Boolean(field.value)}
                                    onChange={field.onChange}
                                    ref={field.ref}
                                    onBlur={field.onBlur}
                                />
                            </FormCheckboxGroup>
                        )}
                    />
                </> : null}
                    </FieldGroup>
                </CardContent>
            </Card>
        </section>
    );
}

function isExactlyTikTok(platforms: Platform[]) {
    return platforms.length === 1 && platforms[0] === "TikTok";
}

export default BookingForm;
